<template>
  <main class="bg-gray-stack-3 text-gray-900 dark:text-gray-50">
    <TransitionRoot as="template" :show="integrationOpenState">
      <Dialog as="div" class="relative z-40">
        <div class="fixed inset-0" />

        <div class="fixed inset-0 overflow-hidden">
          <div class="absolute inset-0 overflow-hidden">
            <div
              class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16"
            >
              <TransitionChild
                as="template"
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enter-from="translate-x-full"
                enter-to="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leave-from="translate-x-0"
                leave-to="translate-x-full"
              >
                <DialogPanel class="pointer-events-auto w-screen max-w-2xl">
                  <div
                    class="flex h-full flex-col overflow-y-scroll bg-gray-stack-1 dark:bg-gray-stack-2 shadow-3d border-l border-hairline"
                  >
                    <div class="px-4 py-6 sm:px-6 bg-gray-stack-1 dark:bg-gray-stack-2 shadow-3d-top border-b border-hairline">
                      <div class="flex items-start justify-between">
                        <DialogTitle class="text-lg font-medium text-gray-900 dark:text-gray-50">
                          Integrations
                        </DialogTitle>
                        <div class="ml-3 flex h-7 items-center">
                          <button
                            type="button"
                            class="rounded-token-sm bg-gray-stack-1 dark:bg-gray-stack-2 text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400 focus:ring-2 focus:ring-[color:var(--ring-primary)] transition-fast"
                            @click="close"
                          >
                            <span class="sr-only">Close panel</span>
                            <XIcon class="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </div>
                    <!-- Main -->
                    <div class="divide-y divide-hairline">
                      <!-- Integration Selection View -->
                      <div v-if="!activeIntegration" class="pb-6">
                        <div class="m-4 p-3 text-gray-900 dark:text-gray-50 rounded-token-lg bg-gray-stack-2 dark:bg-gray-stack-3 shadow-3d border border-hairline">
                          <h3 class="text-lg font-medium mb-4">Available Integrations</h3>
                          
                          <div class="grid grid-cols-1 gap-4">
                            <!-- Square Integration Card -->
                            <IntegrationCard
                              name="Square"
                              description="Connect your Square account to sync payments, invoicing, and customer data"
                              :icon="squareLogo"
                              :is-connected="isSquareConnected"
                              @click="showIntegration('square')"
                            />
                            
                            <!-- Analytics Pixels Card -->
                            <IntegrationCard
                              name="Analytics Pixels"
                              description="Connect Google Analytics, Google Tag Manager, and Facebook Pixel"
                              icon="https://cdn-icons-png.flaticon.com/512/2620/2620240.png"
                              :is-connected="hasAnalyticsConnected"
                              @click="showIntegration('analytics')"
                            />
                            
                            <!-- Meta Pixel Card -->
                            <IntegrationCard
                              name="Meta Pixel"
                              description="Track conversions and optimize your Facebook/Instagram advertising campaigns"
                              :icon="metaIcon"
                              :is-connected="isMetaPixelConnected"
                              @click="showIntegration('metaPixel')"
                            />
                            
                            <!-- Google Analytics Card -->
                            <IntegrationCard
                              name="Google Analytics"
                              description="Send tracking data to Google Analytics—site visits, leads, and bookings from Tint Bolt (server-side events)"
                              :icon="googleAnalyticsLogo"
                              :is-connected="isGoogleAnalyticsConnected"
                              @click="showIntegration('googleAnalytics')"
                            />

                            <!-- TikTok Pixel Card -->
                            <IntegrationCard
                              name="TikTok Pixel"
                              description="Track conversions and optimize your TikTok advertising campaigns via Events API"
                              :icon="tiktokIcon"
                              :is-connected="isTikTokPixelConnected"
                              @click="showIntegration('tiktokPixel')"
                            />
                            
                            <!-- Placeholder for future integrations -->
                            <div class="border border-dashed border-hairline rounded-token-lg p-4 text-center text-gray-500 dark:text-gray-400 bg-gray-stack-2 dark:bg-gray-stack-3">
                              <p class="text-sm">More integrations coming soon...</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <!-- Integration Detail View -->
                      <div v-else class="pb-6">
                        <div class="flex items-center px-4 py-3 border-b border-hairline bg-gray-stack-1 dark:bg-gray-stack-2">
                          <button
                            type="button"
                            class="mr-2 rounded-token-sm bg-gray-stack-1 dark:bg-gray-stack-2 text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400 focus:ring-2 focus:ring-[color:var(--ring-primary)] transition-fast"
                            @click="activeIntegration = null"
                          >
                            <ArrowLeftIcon class="h-5 w-5" aria-hidden="true" />
                          </button>
                          <h3 class="text-lg font-medium text-gray-900 dark:text-gray-50">{{ getIntegrationTitle }}</h3>
                        </div>
                        
                        <div class="m-4 p-3 text-gray-900 dark:text-gray-50 rounded-token-lg bg-gray-stack-2 dark:bg-gray-stack-3 shadow-3d border border-hairline">
                          <SquareIntegration v-if="activeIntegration === 'square'" />
                          <AnalyticsPixels v-if="activeIntegration === 'analytics'" />
                          <MetaPixelIntegration v-if="activeIntegration === 'metaPixel'" />
                          <TikTokPixelIntegration v-if="activeIntegration === 'tiktokPixel'" />
                          <GoogleAnalyticsIntegration v-if="activeIntegration === 'googleAnalytics'" />
                        </div>
                      </div>
                    </div>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </main>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import store from "../../../store";
import { XIcon, ArrowLeftIcon } from "@heroicons/vue/outline";
import AnalyticsPixels from "./AnalyticsPixels.vue";
import SquareIntegration from "./SquareIntegration.vue";
import MetaPixelIntegration from "./MetaPixelIntegration.vue";
import TikTokPixelIntegration from "./TikTokPixelIntegration.vue";
import GoogleAnalyticsIntegration from "./GoogleAnalyticsIntegration.vue";
import IntegrationCard from "./IntegrationCard.vue";
import { projectFirestore } from "../../../firebase/config";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import metaIconImg from '@/assets/integrations/meta-icon.webp';
import tiktokIconImg from '@/assets/integrations/tiktok-icon.png';
import { isTikTokPixelConnected as checkTikTokPixelConfigured } from '@/composables/isTikTokPixelConfigured';

const integrationOpenState = computed(() => {
  return store.state.integrationsOpen;
});

const close = () => {
  store.state.integrationsOpen = false;
};

const activeIntegration = ref(null);
const isSquareConnected = ref(false);
const hasAnalyticsConnected = ref(false);
const isMetaPixelConnected = ref(false);
const isTikTokPixelConnected = ref(false);
const isGoogleAnalyticsConnected = ref(false);
const tiktokPixelIdStatus = ref('');
const tiktokAccessTokenStatus = ref('');
let unsubscribeFirestore = null;

function refreshTikTokConnectedStatus() {
  isTikTokPixelConnected.value = checkTikTokPixelConfigured(
    tiktokPixelIdStatus.value,
    tiktokAccessTokenStatus.value
  );
}

const showIntegration = (integration) => {
  activeIntegration.value = integration;
};

const getIntegrationTitle = computed(() => {
  switch (activeIntegration.value) {
    case 'square':
      return 'Square Integration';
    case 'analytics':
      return 'Analytics Pixels';
    case 'metaPixel':
      return 'Meta Pixel';
    case 'tiktokPixel':
      return 'TikTok Pixel';
    case 'googleAnalytics':
      return 'Google Analytics';
    default:
      return 'Integration';
  }
});

// Define the Square logo as SVG
const squareLogo = `data:image/svg+xml;base64,${btoa('<svg xmlns="http://www.w3.org/2000/svg" width="396" height="396" fill="transparent"><rect x="36" y="36" width="324" height="324" rx="30" ry="30" stroke="#373F4A" stroke-width="72"/><rect x="65" y="65" width="266" height="266" rx="28" ry="28" stroke="#373F4A" stroke-width="14"/><rect x="144" y="144" width="108" height="108" rx="12" ry="12" fill="#373F4A"/></svg>')}`;

// Meta icon
const metaIcon = metaIconImg;

// TikTok icon
const tiktokIcon = tiktokIconImg;

// Define Google Analytics logo as SVG
const googleAnalyticsLogo = `data:image/svg+xml;base64,${btoa('<svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/><path d="M1 1h22v22H1z" fill="none"/></svg>')}`;

// Initial fetch of integration status
const fetchIntegrationStatus = async () => {
  try {
    if (!store.state.currentUser) {
      return;
    }
    
    const userDocRef = doc(projectFirestore, 'users', store.state.currentUser.uid);
    const configDocRef = doc(userDocRef, 'config', 'integrations');
    
    const docSnap = await getDoc(configDocRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      
      // Check Analytics connections
      hasAnalyticsConnected.value = !!(
        (data.googleAnalytics4 && data.googleAnalytics4.length > 0) ||
        (data.googleTagManager && data.googleTagManager.length > 0) ||
        (data.facebookPixel && data.facebookPixel.length > 0)
      );
    }
    
    // Check Meta Pixel connection from companyProfile
    if (store.state.companyProfileName) {
      try {
        const companyProfileRef = doc(projectFirestore, 'companyProfile', store.state.companyProfileName);
        const companyProfileSnap = await getDoc(companyProfileRef);
        
        if (companyProfileSnap.exists()) {
          const companyData = companyProfileSnap.data();
          isMetaPixelConnected.value = !!(companyData.config?.facebookPixel && companyData.config.facebookPixel.length > 5);
          tiktokPixelIdStatus.value = companyData.config?.tiktokPixelId || '';
          refreshTikTokConnectedStatus();
        }
      } catch (error) {
        console.error('Error checking Meta Pixel connection:', error);
      }
    }
    
    // Check Square connection
    const userSnap = await getDoc(userDocRef);
    if (userSnap.exists()) {
      const userData = userSnap.data();
      const isConnected = !!(userData.square?.connected && userData.tokenSecure);
      isSquareConnected.value = isConnected;
      // Update the store state
      store.commit('SET_SQUARE_CONNECTED', isConnected);
      
      // Check Google Analytics connection
      isGoogleAnalyticsConnected.value = !!(userData.config?.googleAnalytics?.connected);

      tiktokAccessTokenStatus.value = userData.config?.tiktokPixelAccessToken || '';
      refreshTikTokConnectedStatus();
    }
  } catch (error) {
    console.error('Error fetching integration data:', error);
  }
};

// Set up real-time listeners
onMounted(async () => {
  await fetchIntegrationStatus();
  
    // Set up real-time listener for Square connection status
    if (store.state.currentUser && store.state.currentUser.uid) {
      const userDocRef = doc(projectFirestore, 'users', store.state.currentUser.uid);
      
      unsubscribeFirestore = onSnapshot(userDocRef, (docSnap) => {
        if (docSnap.exists()) {
          const userData = docSnap.data();
          const isConnected = !!(userData.square?.connected && userData.tokenSecure);
          isSquareConnected.value = isConnected;
          // Update the store state
          store.commit('SET_SQUARE_CONNECTED', isConnected);
          
          // Update Google Analytics connection status
          isGoogleAnalyticsConnected.value = !!(userData.config?.googleAnalytics?.connected);

          tiktokAccessTokenStatus.value = userData.config?.tiktokPixelAccessToken || '';
          refreshTikTokConnectedStatus();
        } else {
          isSquareConnected.value = false;
          store.commit('SET_SQUARE_CONNECTED', false);
          isGoogleAnalyticsConnected.value = false;
          tiktokAccessTokenStatus.value = '';
          refreshTikTokConnectedStatus();
        }
      }, (error) => {
        console.error('Error listening to Square connection status:', error);
      });
    }
    
    // Set up real-time listener for Meta Pixel connection status
    if (store.state.companyProfileName) {
      const companyProfileRef = doc(projectFirestore, 'companyProfile', store.state.companyProfileName);
      
      onSnapshot(companyProfileRef, (docSnap) => {
        if (docSnap.exists()) {
          const companyData = docSnap.data();
          isMetaPixelConnected.value = !!(companyData.config?.facebookPixel && companyData.config.facebookPixel.length > 5);
          tiktokPixelIdStatus.value = companyData.config?.tiktokPixelId || '';
          refreshTikTokConnectedStatus();
        } else {
          isMetaPixelConnected.value = false;
          tiktokPixelIdStatus.value = '';
          refreshTikTokConnectedStatus();
        }
      }, (error) => {
        console.error('Error listening to Meta Pixel connection status:', error);
      });
    }
});

// Clean up listeners on component unmount
onUnmounted(() => {
  if (unsubscribeFirestore) {
    unsubscribeFirestore();
  }
});

let userConfig = computed(() => {
  return store.state.userConfig;
});
</script>
